import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PlantingSiteDto } from '../../../models/trofaco/plantingSiteDto';
import { PageChangedEvent, PaginationComponent } from 'ngx-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, skip, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { ArrayHelper } from '../../../models/helpers/array-helper';
import { ConfirmDeleteModalComponent } from '../../core/components/delete-modal/confirm-delete-modal-component';
import { PlantingSiteService } from '../../core/services/planting-site.service';
import { BaseComponent } from '../../core/components/bases/base-component';
import { UserService } from '../../core/services/user.service';
import { AppUserModel } from 'src/app/models/users/app-user.model';

type getColumnValue = (site: PlantingSiteDto) => number | string | undefined;
type Column = { key: string, property: getColumnValue, title: string, textRight?: boolean };

@Component({
    selector: 'app-site-table',
    templateUrl: './site-table.component.html',
    providers: [PlantingSiteService]
})
export class SiteTableComponent extends BaseComponent implements OnInit {

    @ViewChild(PaginationComponent, {static: true}) pagination: PaginationComponent;
    @ViewChild(ConfirmDeleteModalComponent, {static: false}) confirmDeleteModal: ConfirmDeleteModalComponent;

    pageSites: PlantingSiteDto[] = [];
    _sites: PlantingSiteDto[] = [];

    @Input('sites')
    set sites(sites: PlantingSiteDto[]) {
        this._sites = sites;
        this.pagination.selectPage(1);
        this.sortByChanged();
    }

    @Input()
    pageSizeUpdate$: Observable<number>;

    @Output() siteDeleted = new EventEmitter<PlantingSiteDto>();

    columns: Column[] = [
        {key: 'siteNo', title: 'Planting Site #', property: site => site.code},
        {key: 'name', title: 'Name of Site', property: site => site.title},
        {key: 'country', title: 'Country', property: site => site.country},
        {key: 'region', title: 'Region', property: site => site.region},
        {key: 'year', title: 'Year', property: site => new Date(site.plantingDate).getFullYear(), textRight: true},
        {key: 'photo', title: 'Photo Collection', property: site => site.photoCollectionCount, textRight: true},
        {key: 'planted', title: 'Planted Trees', property: site => site.totalTrees, textRight: true},
        {key: 'available', title: 'Available Trees', property: site => site.availableTrees, textRight: true},
    ];
    sortColumn = this.columns[0];
    sortAsc = true;
    currentUser: AppUserModel;

    @Input()
    pageSize: number;
    scrollConfig = {
        suppressScrollX: false,
        suppressScrollY: true
    };

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private userService: UserService
        ) {
            super();
    }

    ngOnInit() {
        this.getCurrentUser();
        this.route.queryParamMap
            .pipe(
                map(map => map.get('page') || '1'),
                distinctUntilChanged()
            )
            .subscribe(page => {
                this.pagination.selectPage(parseInt(page));
            });

        this.pagination.pageChanged.subscribe((event: PageChangedEvent) => {
           let {page, ...params} = this.route.snapshot.queryParams;
           if (event.page !== 1) params.page = event.page;
            this.router.navigate([],
                {
                    replaceUrl: true,
                    relativeTo: this.route,
                    queryParams: params
                });
        });

        let sort$ = this.route.queryParamMap
            .pipe(
                map(map => map.get('sort') || this.columns[0].key),
                distinctUntilChanged()
            );

        let sortAsc$ = this.route.queryParamMap
            .pipe(
                map(map => map.get('sortAsc') || 'true'),
                distinctUntilChanged()
            );

        combineLatest([sort$, sortAsc$]).subscribe(([sortKey, sortAsc]) => {
            let column = this.columns.find(value => value.key == sortKey);
            if (column) {
                this.sortColumn = column;
            }
            this.sortAsc = sortAsc === 'true';
            this.sortByChanged();
        });

        this.pageSizeUpdate$.pipe(takeUntil(this.onDestroy$)).subscribe((pageSize) => {
            this.pageSize = pageSize;
            this.pagination.selectPage(1);
            this.pageChange(1);
        });
    }

    sortBy(column: Column) {

        if (this.sortColumn == column) {
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortColumn = column;
            this.sortAsc = true;
        }
        let sortKey = this.sortColumn.key;

        this.router.navigate([],
            {
                queryParamsHandling: 'merge',
                replaceUrl: true,
                relativeTo: this.route,
                queryParams: {'sort': sortKey, 'sortAsc': this.sortAsc}
            });
    }

    sortByChanged() {
        this._sites = ArrayHelper.sortBy(this._sites, this.sortAsc, this.sortColumn.property);
        this.pageChange();

    }

    pageChange(page: number = this.pagination.page) {
        let start = this.pageSize * (page - 1);
        let end = this.pageSize * page;
        this.pageSites = this._sites.slice(start, end);
    }

    async showDeleteConfirmModal(site: PlantingSiteDto){
        let result = await this.confirmDeleteModal.showAwait();
        if (result){
            this.siteDeleted.emit(site)
        }
    }

    async getCurrentUser() {
        this.currentUser = await this.userService.getCurrentUserAsync();
    }

}
